import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { UrlService } from '../../../../../environments/urlService';
import { Observable } from 'rxjs';
import { IGNORE_ERROR_HANDLING, IS_SPINNER_ACTIVE } from '../../../../util/interceptors';
import { UUID } from '../../../../core/pojo/id-variable';
import {
  KpiAnalysisChartDTO, KpiAnalysisChartSetUp,
  MetricPortfolioAnalysis,
  MetricPortfolioAnalysisWrapper,
} from '../../dto/MetricPortfolioAnalysis';
import { map } from 'rxjs/operators';
import { DateService } from '../../../../util/common/date-service/date.service';
import {
  KpiAnalysisTimeFrame,
} from "../../components/kpi-metrics-main/portfolio-analysis/kpi-portfolio-analysis.component";
import {
  KpiPortfolioAnalysisChartOptionsService,
} from "../../components/kpi-metrics-main/portfolio-analysis/service/KpiPortfolioAnalysisChartOptionsService";
import { HighchartsOptions } from "../../../shared/component/pie-chart-simple/highcharts-common/highcharts-global-const";

@Injectable({
  providedIn: 'root',
})
export class PortfolioAnalysisService {
  private readonly url: string;

  constructor(
    private http: HttpClient,
    private urlService: UrlService,
  ) {
    this.url = this.urlService.getFmsUrl() + 'kpi/metrics/analysis';
  }

  getWrapper(orgUUID: UUID, timeFrame: KpiAnalysisTimeFrame): Observable<MetricPortfolioAnalysisWrapper> {
    return this.http.get<MetricPortfolioAnalysisWrapper>(
      this.url + '/wrapper',
      {
        params: { orgUUID, timeFrame },
        context: new HttpContext()
          .set(IS_SPINNER_ACTIVE, true)
          .set(IGNORE_ERROR_HANDLING, true), // org does not have analysis wrapper by default.
        withCredentials: true,
      },
    );
  }

  putTimePeriod(orgUUID: UUID, timeFrame: KpiAnalysisTimeFrame, userId: number): Observable<void> {
    return this.http.put<void>(
      this.url + '/time-period', {},
      {
        params: { orgUUID, timeFrame, userId },
        context: new HttpContext()
          .set(IS_SPINNER_ACTIVE, false)
          .set(IGNORE_ERROR_HANDLING, true),
        withCredentials: true,
      },
    );
  }

  getTimePeriod(orgUUID: UUID, userId: number): Observable<KpiAnalysisTimeFrame> {
    return this.http.get<KpiAnalysisTimeFrame>(
      this.url + '/time-period',
      {
        params: { orgUUID, userId },
        context: new HttpContext()
          .set(IS_SPINNER_ACTIVE, false)
          .set(IGNORE_ERROR_HANDLING, true),
        withCredentials: true,
      },
    );
  }

  getIsAnalysisProvided(orgUUID: UUID): Observable<boolean> {
    return this.http.get<boolean>(
      this.url + '/is-provided',
      {
        params: { orgUUID },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      },
    );
  }

  get(orgUUID: UUID): Observable<MetricPortfolioAnalysis> {
    return this.http.get<MetricPortfolioAnalysis>(
      this.url,
      {
        params: { orgUUID },
        context: new HttpContext()
          .set(IS_SPINNER_ACTIVE, true)
          .set(IGNORE_ERROR_HANDLING, true), // org does not have analysis wrapper by default.
        withCredentials: true,
      },
    ).pipe(
      // set all periods to be without a day. E.x. from 2020-12-31 to 2020-12
      map((analysis) => {
        analysis.bundles.forEach((bundle) => {
          bundle.period = DateService.getMonthWithoutDay(bundle.period);
        });
        return analysis;
      }),
    );
  }

  save(analysis: MetricPortfolioAnalysis): Observable<void> {
    return this.http.put<void>(this.url,
      analysis, {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      });
  }

  updateIsTrackedStatus(companyUUID: UUID, isKpiPortfolioTracked: boolean): Observable<void> {
    return this.http.put<void>(this.url + '/companies/tracked-status',
      null, {
        params: { companyUUID, isKpiPortfolioTracked },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      });
  }

  getKpiPortfolioAnalysisChartData(setUp: KpiAnalysisChartSetUp): Observable<HighchartsOptions> {
    return this.http.put<KpiAnalysisChartDTO>(this.url + '/chart', setUp, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
      withCredentials: true,
    }).pipe(
      map((chartData: KpiAnalysisChartDTO): HighchartsOptions => {
        return KpiPortfolioAnalysisChartOptionsService.getOptions(chartData, setUp.metricName, setUp.isTotal);
      })
    );
  }
}
